import { useState, useEffect } from 'react';
import PageTitle from '../components/PageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Loader2Icon, PlayIcon, PlusIcon, PencilIcon, TrashIcon, HistoryIcon } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import ApiService from '../services/api.service';
import BackgroundService from '../services/bg.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface Agent {
  id: string;
  name: string;
  prompt: string;
  schedule: {
    cron_expression?: string;
    timezone?: string;
    enabled: boolean;
  } | null;
  agent_type: string;
  model: string;
  initial_question: string;
  created_at: string;
  updated_at: string;
}

interface RunningJob {
  agentId: string;
  jobId: string;
  status: 'queued' | 'started' | 'completed' | 'failed';
  threadId?: string;
}

interface AgentExecution {
  thread_id: string;
  name: string;
  agent_type: string;
  created_at: string;
  updated_at: string;
}

export default function Agents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [runningJobs, setRunningJobs] = useState<Map<string, RunningJob>>(new Map());
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [isExecutionsSheetOpen, setIsExecutionsSheetOpen] = useState(false);
  const [executions, setExecutions] = useState<AgentExecution[]>([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    prompt: '',
    schedule: {
      cron_expression: '0 2 * * 1', // Default: 2am every Monday
      timezone: 'America/New_York',
      enabled: false,
    },
    agent_type: 'analyst',
    model: 'claude-4-sonnet',
    initial_question: '',
  });

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getInstance().request('GET', '/agents') as { agents: Agent[] };
      setAgents(response.agents);
    } catch (error) {
      console.error('Failed to load agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAgent(null);
    setFormData({
      name: '',
      prompt: '',
      schedule: {
        cron_expression: '0 2 * * 1',
        timezone: 'America/New_York',
        enabled: false,
      },
      agent_type: 'analyst',
      model: 'claude-4-sonnet',
      initial_question: '',
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (agent: Agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      prompt: agent.prompt,
      schedule: {
        cron_expression: agent.schedule?.cron_expression || '0 2 * * 1',
        timezone: agent.schedule?.timezone || 'America/New_York',
        enabled: agent.schedule?.enabled || false,
      },
      agent_type: agent.agent_type,
      model: agent.model,
      initial_question: agent.initial_question,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingAgent) {
        await ApiService.getInstance().request('PATCH', `/agents/${editingAgent.id}`, formData);
        toast.success('Agent updated successfully');
      } else {
        await ApiService.getInstance().request('POST', '/agents', formData);
        toast.success('Agent created successfully');
      }
      setIsDialogOpen(false);
      loadAgents();
    } catch (error) {
      console.error('Failed to save agent:', error);
      toast.error('Failed to save agent');
    }
  };

  const handleDelete = async (agentId: string) => {
    if (!confirm('Are you sure you want to delete this agent?')) {
      return;
    }

    try {
      await ApiService.getInstance().request('DELETE', `/agents/${agentId}`);
      toast.success('Agent deleted successfully');
      loadAgents();
    } catch (error) {
      console.error('Failed to delete agent:', error);
      toast.error('Failed to delete agent');
    }
  };

  const handleRunNow = async (agent: Agent) => {
    try {
      const response = await ApiService.getInstance().request('POST', `/agents/${agent.id}/run`, {
        initial_question: agent.initial_question,
      }) as { job_id: string; agent_id: string; status: string };

      // Track the running job
      const runningJob: RunningJob = {
        agentId: agent.id,
        jobId: response.job_id,
        status: 'queued',
      };
      setRunningJobs(new Map(runningJobs.set(agent.id, runningJob)));

      toast.success('Agent run started');

      // Poll for job completion
      const bgService = BackgroundService.getInstance();
      bgService.waitForJob(
        response.job_id,
        false,
        (progress) => {
          setRunningJobs((prev) => {
            const newMap = new Map(prev);
            const job = newMap.get(agent.id);
            if (job) {
              job.status = progress.status as any;
              newMap.set(agent.id, job);
            }
            return newMap;
          });
        }
      ).then((result: any) => {
        if (result && result.thread_id) {
          setRunningJobs((prev) => {
            const newMap = new Map(prev);
            const job = newMap.get(agent.id);
            if (job) {
              job.status = 'completed';
              job.threadId = result.thread_id;
              newMap.set(agent.id, job);
            }
            return newMap;
          });
          toast.success('Agent run completed! Click to view conversation.');
        }
      }).catch((error) => {
        console.error('Job failed:', error);
        setRunningJobs((prev) => {
          const newMap = new Map(prev);
          const job = newMap.get(agent.id);
          if (job) {
            job.status = 'failed';
            newMap.set(agent.id, job);
          }
          return newMap;
        });
        toast.error('Agent run failed');
      });
    } catch (error) {
      console.error('Failed to run agent:', error);
      toast.error('Failed to start agent run');
    }
  };

  const handleViewConversation = (threadId: string) => {
    navigate(`/ellie/conversation/${threadId}`);
  };

  const getJobStatus = (agentId: string): RunningJob | undefined => {
    return runningJobs.get(agentId);
  };

  const handleViewExecutions = async (agent: Agent) => {
    setSelectedAgent(agent);
    setIsExecutionsSheetOpen(true);
    await loadExecutions(agent.id);
  };

  const loadExecutions = async (agentId: string) => {
    try {
      setLoadingExecutions(true);
      const response = await ApiService.getInstance().request('GET', `/agents/${agentId}/executions`) as { executions: AgentExecution[] };
      setExecutions(response.executions);
    } catch (error) {
      console.error('Failed to load executions:', error);
      toast.error('Failed to load executions');
    } finally {
      setLoadingExecutions(false);
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2Icon className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <PageTitle title="Agents" className="mb-2" titleClassName="text-3xl font-bold" />
          <p className="text-muted-foreground">Manage AI agents that run conversations in the background</p>
        </div>
        <Button onClick={handleCreate}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Agent
        </Button>
      </div>

      {agents.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">No agents yet. Create your first agent to get started.</p>
          <Button onClick={handleCreate}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Agent
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => {
                const job = getJobStatus(agent.id);
                return (
                  <TableRow key={agent.id}>
                    <TableCell className="font-medium">{agent.name}</TableCell>
                    <TableCell>{agent.agent_type}</TableCell>
                    <TableCell>{agent.model}</TableCell>
                    <TableCell>
                      {agent.schedule?.enabled ? (
                        <span className="text-sm text-muted-foreground">
                          {agent.schedule.cron_expression}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not scheduled</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {job && (
                        <div className="flex items-center gap-2">
                          {job.status === 'queued' || job.status === 'started' ? (
                            <>
                              <Loader2Icon className="h-4 w-4 animate-spin" />
                              <span className="text-sm">Running...</span>
                            </>
                          ) : job.status === 'completed' ? (
                            <span className="text-sm text-success">Completed</span>
                          ) : job.status === 'failed' ? (
                            <span className="text-sm text-destructive">Failed</span>
                          ) : null}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewExecutions(agent)}
                        >
                          <HistoryIcon className="h-4 w-4 mr-1" />
                          Executions
                        </Button>
                        {job?.status === 'completed' && job.threadId && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewConversation(job.threadId!)}
                          >
                            View
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRunNow(agent)}
                          disabled={job?.status === 'queued' || job?.status === 'started'}
                        >
                          {job?.status === 'queued' || job?.status === 'started' ? (
                            <Loader2Icon className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <PlayIcon className="h-4 w-4 mr-1" />
                              Run Now
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(agent)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(agent.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAgent ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
            <DialogDescription>
              Configure your AI agent. The prompt will be appended to the system prompt when running in analyst mode.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="My Agent"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prompt">System Prompt</Label>
              <Textarea
                id="prompt"
                value={formData.prompt}
                onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                placeholder="Additional instructions for the agent..."
                rows={6}
              />
              <p className="text-sm text-muted-foreground">
                This prompt will be appended to the main system prompt when running in analyst mode.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent_type">Agent Type</Label>
              <select
                id="agent_type"
                value={formData.agent_type}
                onChange={(e) => setFormData({ ...formData, agent_type: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="analyst">Analyst</option>
                <option value="build_chart">Build Chart</option>
                <option value="infer_data_model">Infer Data Model</option>
                <option value="onboarding">Onboarding</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <select
                id="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="claude-4-sonnet">Claude 4 Sonnet</option>
                <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                <option value="sf-llama3.1-70b">Llama 3.1 70B</option>
                <option value="sf-llama3.3-70b">Llama 3.3 70B</option>
                <option value="sf-mistral-large2">Mistral Large 2</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="initial_question">Initial Question</Label>
              <Input
                id="initial_question"
                value={formData.initial_question}
                onChange={(e) => setFormData({ ...formData, initial_question: e.target.value })}
                placeholder="What question should the agent start with?"
              />
            </div>

            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="schedule_enabled"
                  checked={formData.schedule.enabled}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      schedule: { ...formData.schedule, enabled: e.target.checked },
                    })
                  }
                  className="rounded border-gray-300"
                />
                <Label htmlFor="schedule_enabled">Enable Schedule</Label>
              </div>

              {formData.schedule.enabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="cron_expression">Cron Expression</Label>
                    <Input
                      id="cron_expression"
                      value={formData.schedule.cron_expression}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          schedule: { ...formData.schedule, cron_expression: e.target.value },
                        })
                      }
                      placeholder="0 2 * * 1"
                    />
                    <p className="text-sm text-muted-foreground">
                      Cron expression (e.g., "0 2 * * 1" for 2am every Monday)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Input
                      id="timezone"
                      value={formData.schedule.timezone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          schedule: { ...formData.schedule, timezone: e.target.value },
                        })
                      }
                      placeholder="America/New_York"
                    />
                  </div>
                </>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={isExecutionsSheetOpen} onOpenChange={setIsExecutionsSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>
              {selectedAgent ? `${selectedAgent.name} - Executions` : 'Agent Executions'}
            </SheetTitle>
            <SheetDescription>
              View all conversation executions for this agent
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {loadingExecutions ? (
              <div className="flex items-center justify-center py-12">
                <Loader2Icon className="h-8 w-8 animate-spin" />
              </div>
            ) : executions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No executions yet. Run the agent to create conversations.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {executions.map((execution) => (
                  <div
                    key={execution.thread_id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent cursor-pointer"
                    onClick={() => {
                      setIsExecutionsSheetOpen(false);
                      handleViewConversation(execution.thread_id);
                    }}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{execution.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {formatTimeAgo(execution.updated_at)} • {formatDateTime(execution.created_at)}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      View
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
